/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
/* eslint-disable no-underscore-dangle */

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import MDBox from "components/MDBox";
import {
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  FormGroup,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DataTable from "examples/Tables/DataTable";
import FilterDropdown from "components/Dropdown/FilterDropdown";
import CustomSearch from "components/Search/Search";
import ResetFilterButton from "components/Buttons/ResetButton";
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";
import ConfirmationModal from "examples/modal/Confirmation/Confirmation";
import pxToRem from "assets/theme/functions/pxToRem";
import Constants, { Icons, Colors, defaultData } from "utils/Constants";
import { useDispatch, useSelector } from "react-redux";
import { openSnackbar } from "redux/Slice/Notification";

import insuranceTableData from "dataTables/insurance/insuranceTable";
import AddInsuranceModal, {
  INSURANCE_PRODUCTS,
  INSURANCE_CODES,
  POLICY_STATUSES,
  MONTH_OPTIONS,
} from "examples/modal/InsuranceModal/AddInsuranceModal";
import EditInsuranceModal from "examples/modal/InsuranceModal/EditInsuranceModal";
import ViewInsuranceModal from "examples/modal/InsuranceModal/ViewInsuranceModal";

import {
  createInsuranceThunk,
  getAllInsuranceThunk,
  updateInsuranceThunk,
  deleteInsuranceThunk,
  exportInsuranceThunk,
} from "redux/Thunks/Insurance";
import {
  addInsuranceToList,
  updateInsuranceInList,
  removeInsuranceFromList,
} from "redux/Slice/Insurance.slice";

const DEFAULT_VISIBLE_COLUMNS = [
  "srNo",
  "month",
  "inquiryDate",
  "policyIssueDate",
  "customerName",
  "mobileNumber",
  "product",
  "grossPremium",
  "netPremium",
  "policyStatus",
  "policyNumber",
  "insuranceCompany",
  "code",
  "attachments",
  "action",
];

const INSURANCE_COLUMN_VISIBILITY_KEY = "insuranceVisibleColumns";
const INSURANCE_FILTER_VISIBILITY_KEY = "insuranceVisibleFilters";

const DEFAULT_VISIBLE_FILTER_IDS = [
  "product",
  "code",
  "insuranceCompany",
  "month",
  "financialYear",
  "policyStatus",
  "customerName",
  "mobileNumber",
  "policyNumber",
];

const getFYOptions = () => {
  const now = new Date();
  const currentFYStart = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
  const list = [];
  for (let y = currentFYStart + 1; y >= currentFYStart - 3; y -= 1) {
    list.push(`${y}-${String(y + 1).slice(-2)}`);
  }
  if (!list.includes("2026-27")) list.push("2026-27");
  if (!list.includes("2025-26")) list.push("2025-26");
  return list;
};

const getSavedVisibleItems = (key) => {
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    return Array.isArray(saved) ? saved : null;
  } catch (error) {
    return null;
  }
};

const PRODUCT_FILTER_OPTIONS = [
  { [Constants.MONGOOSE_ID]: "all", title: "All Products" },
  ...INSURANCE_PRODUCTS.map((prod) => ({
    [Constants.MONGOOSE_ID]: prod,
    title: prod,
  })),
];

const CODE_FILTER_OPTIONS = [
  { [Constants.MONGOOSE_ID]: "all", title: "All Codes" },
  ...INSURANCE_CODES.map((code) => ({
    [Constants.MONGOOSE_ID]: code,
    title: code,
  })),
];

const MONTH_FILTER_OPTIONS = [
  { [Constants.MONGOOSE_ID]: "all", title: "All Months" },
  ...MONTH_OPTIONS.map((m) => ({
    [Constants.MONGOOSE_ID]: m,
    title: m,
  })),
];

const FY_FILTER_OPTIONS = [
  { [Constants.MONGOOSE_ID]: "all", title: "All FY" },
  ...getFYOptions().map((fy) => ({
    [Constants.MONGOOSE_ID]: fy,
    title: fy,
  })),
];

const STATUS_FILTER_OPTIONS = [
  { [Constants.MONGOOSE_ID]: "all", title: "All Statuses" },
  ...POLICY_STATUSES.map((st) => ({
    [Constants.MONGOOSE_ID]: st,
    title: st,
  })),
];

export default function InsuranceListing() {
  const dispatch = useDispatch();

  const insuranceState = useSelector((state) => state.insurance);
  const insuranceList = insuranceState?.insuranceList || [];
  const loading = insuranceState?.loading || Constants.PENDING;

  // Modals state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, insurance: null, loading: false });
  const [viewModal, setViewModal] = useState({ open: false, insurance: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, insurance: null, loading: false });

  // Pagination state
  const [tablePagination, setTablePagination] = useState({
    page: 0,
    perPage: defaultData.DATE_ON_SINGLE_API_CALL,
  });
  const [next, setNext] = useState(0);

  // Filter values
  const [filters, setFilters] = useState({
    product: "all",
    code: "all",
    month: "all",
    financialYear: "all",
    policyStatus: "all",
  });

  // Search values
  const [searchQueries, setSearchQueries] = useState({
    customerName: "",
    mobileNumber: "",
    policyNumber: "",
    insuranceCompany: "",
    imd: "",
    reference: "",
  });

  // Column visibility state
  const savedColumns = useMemo(
    () => getSavedVisibleItems(INSURANCE_COLUMN_VISIBILITY_KEY) || DEFAULT_VISIBLE_COLUMNS,
    []
  );
  const [visibleColumns, setVisibleColumns] = useState(savedColumns);

  // Filter visibility state
  const savedFilterIds = useMemo(
    () => getSavedVisibleItems(INSURANCE_FILTER_VISIBILITY_KEY) || DEFAULT_VISIBLE_FILTER_IDS,
    []
  );
  const [visibleFilterIds, setVisibleFilterIds] = useState(savedFilterIds);

  // Drawer state
  const [manageDrawer, setManageDrawer] = useState({ open: false, type: "columns" });
  const [tempVisibleColumns, setTempVisibleColumns] = useState(visibleColumns);
  const [tempVisibleFilterIds, setTempVisibleFilterIds] = useState(visibleFilterIds);

  const debounceTimeout = useRef(null);

  // Build query string
  const buildQuery = useCallback(
    (page = tablePagination.page, perPage = tablePagination.perPage) => {
      const parts = [`page=${page}`, `perPage=${perPage}`];

      if (filters.product && filters.product !== "all") {
        parts.push(`product=${encodeURIComponent(filters.product)}`);
      }
      if (filters.code && filters.code !== "all") {
        parts.push(`code=${encodeURIComponent(filters.code)}`);
      }
      if (filters.month && filters.month !== "all") {
        parts.push(`month=${encodeURIComponent(filters.month)}`);
      }
      if (filters.financialYear && filters.financialYear !== "all") {
        parts.push(`financialYear=${encodeURIComponent(filters.financialYear)}`);
      }
      if (filters.policyStatus && filters.policyStatus !== "all") {
        parts.push(`policyStatus=${encodeURIComponent(filters.policyStatus)}`);
      }

      if (searchQueries.customerName && searchQueries.customerName.trim()) {
        parts.push(`customerName=${encodeURIComponent(searchQueries.customerName.trim())}`);
      }
      if (searchQueries.mobileNumber && searchQueries.mobileNumber.trim()) {
        parts.push(`mobileNumber=${encodeURIComponent(searchQueries.mobileNumber.trim())}`);
      }
      if (searchQueries.policyNumber && searchQueries.policyNumber.trim()) {
        parts.push(`policyNumber=${encodeURIComponent(searchQueries.policyNumber.trim())}`);
      }
      if (searchQueries.insuranceCompany && searchQueries.insuranceCompany.trim()) {
        parts.push(`insuranceCompany=${encodeURIComponent(searchQueries.insuranceCompany.trim())}`);
      }
      if (searchQueries.imd && searchQueries.imd.trim()) {
        parts.push(`imd=${encodeURIComponent(searchQueries.imd.trim())}`);
      }
      if (searchQueries.reference && searchQueries.reference.trim()) {
        parts.push(`reference=${encodeURIComponent(searchQueries.reference.trim())}`);
      }

      return parts.join("&");
    },
    [tablePagination.page, tablePagination.perPage, filters, searchQueries]
  );

  // Fetch insurances
  const fetchInsurances = useCallback(
    async (page, perPage) => {
      const queryString = buildQuery(page, perPage);
      await dispatch(getAllInsuranceThunk(queryString));
    },
    [dispatch, buildQuery]
  );

  useEffect(() => {
    fetchInsurances(tablePagination.page, tablePagination.perPage);
  }, [fetchInsurances, tablePagination.page, tablePagination.perPage]);

  const handleTablePagination = async () => {
    const queryString = buildQuery(next + 1, tablePagination.perPage);
    const res = await dispatch(getAllInsuranceThunk(queryString));
    if (res?.payload?.status === 200) {
      const payloadData = res.payload.data?.data;
      let list = [];
      if (Array.isArray(payloadData?.data)) {
        list = payloadData.data;
      } else if (Array.isArray(payloadData)) {
        list = payloadData;
      }
      setNext(list.length > 0 ? next + 1 : next);
    }
  };

  // Handlers for Modals
  const handleViewModal = (insurance) => {
    setViewModal({ open: true, insurance });
  };

  const handleEditModal = (insurance) => {
    setEditModal({ open: true, insurance, loading: false });
  };

  const handleDeleteModal = (insurance) => {
    setDeleteModal({ open: true, insurance, loading: false });
  };

  const handleViewAttachments = (insurance) => {
    setViewModal({ open: true, insurance });
  };

  // Submit Create
  const handleConfirmCreate = async (payload) => {
    setCreateLoading(true);
    try {
      const res = await dispatch(createInsuranceThunk(payload));
      if (res.payload && (res.payload.status === 200 || res.payload.status === 201)) {
        dispatch(
          openSnackbar({
            message: "Insurance policy created successfully!",
            notificationType: "success",
          })
        );
        if (res.payload.data) {
          dispatch(addInsuranceToList(res.payload.data));
        }
        setAddModalOpen(false);
        setNext(0);
        setTablePagination((prev) => ({ ...prev, page: 0 }));
        fetchInsurances(0, tablePagination.perPage);
      } else {
        const errorMsg =
          res.payload?.message || res.error?.message || "Failed to create insurance policy";
        dispatch(
          openSnackbar({
            message: errorMsg,
            notificationType: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        openSnackbar({
          message: "An unexpected error occurred while creating policy",
          notificationType: "error",
        })
      );
    } finally {
      setCreateLoading(false);
    }
  };

  // Submit Edit
  const handleConfirmEdit = async (payload) => {
    if (!editModal.insurance?._id) return;
    setEditModal((prev) => ({ ...prev, loading: true }));
    try {
      const res = await dispatch(
        updateInsuranceThunk({
          id: editModal.insurance._id,
          body: payload,
        })
      );
      if (res.payload && (res.payload.status === 200 || res.payload.status === 201)) {
        dispatch(
          openSnackbar({
            message: "Insurance policy updated successfully!",
            notificationType: "success",
          })
        );
        if (res.payload.data) {
          dispatch(updateInsuranceInList(res.payload.data));
        }
        setEditModal({ open: false, insurance: null, loading: false });
        setNext(0);
        setTablePagination((prev) => ({ ...prev, page: 0 }));
        fetchInsurances(0, tablePagination.perPage);
      } else {
        const errorMsg =
          res.payload?.message || res.error?.message || "Failed to update insurance policy";
        dispatch(
          openSnackbar({
            message: errorMsg,
            notificationType: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        openSnackbar({
          message: "An unexpected error occurred while updating policy",
          notificationType: "error",
        })
      );
    } finally {
      setEditModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Submit Delete
  const handleConfirmDelete = async () => {
    if (!deleteModal.insurance?._id) return;
    setDeleteModal((prev) => ({ ...prev, loading: true }));
    try {
      const res = await dispatch(deleteInsuranceThunk(deleteModal.insurance._id));
      if (res.payload && res.payload.status === 200) {
        dispatch(
          openSnackbar({
            message: "Insurance policy deleted successfully!",
            notificationType: "success",
          })
        );
        dispatch(removeInsuranceFromList(deleteModal.insurance._id));
        setDeleteModal({ open: false, insurance: null, loading: false });
        fetchInsurances(tablePagination.page, tablePagination.perPage);
      } else {
        const errorMsg =
          res.payload?.message || res.error?.message || "Failed to delete insurance policy";
        dispatch(
          openSnackbar({
            message: errorMsg,
            notificationType: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        openSnackbar({
          message: "An unexpected error occurred while deleting policy",
          notificationType: "error",
        })
      );
    } finally {
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Export to Excel
  const handleExport = async () => {
    try {
      const currentDate = new Date();
      const filename = `Finvibes_Insurance_${currentDate.getFullYear()}-${
        currentDate.getMonth() + 1
      }-${currentDate.getDate()}_${currentDate.getHours()}-${currentDate.getMinutes()}-${currentDate.getSeconds()}.xlsx`;

      const queryString = buildQuery(0, 10000);
      const res = await dispatch(exportInsuranceThunk(queryString));
      const url = window.URL.createObjectURL(new Blob([res.payload]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      dispatch(
        openSnackbar({
          message: "Insurance data exported successfully!",
          notificationType: "success",
        })
      );
    } catch (err) {
      dispatch(
        openSnackbar({
          message: "Failed to export insurance data",
          notificationType: "error",
        })
      );
    }
  };

  // Filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setNext(0);
    setTablePagination((prev) => ({ ...prev, page: 0 }));
  };

  // Debounced search
  const handleSearchChange = (field, value) => {
    setSearchQueries((prev) => ({ ...prev, [field]: value }));
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setNext(0);
      setTablePagination((prev) => ({ ...prev, page: 0 }));
    }, 400);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters({
      product: "all",
      code: "all",
      month: "all",
      financialYear: "all",
      policyStatus: "all",
    });
    setSearchQueries({
      customerName: "",
      mobileNumber: "",
      policyNumber: "",
      insuranceCompany: "",
      imd: "",
      reference: "",
    });
    setNext(0);
    setTablePagination((prev) => ({ ...prev, page: 0 }));
  };

  // Drawer handlers
  const handleOpenDrawer = (type) => {
    if (type === "columns") {
      setTempVisibleColumns([...visibleColumns]);
    } else {
      setTempVisibleFilterIds([...visibleFilterIds]);
    }
    setManageDrawer({ open: true, type });
  };

  const handleCloseDrawer = () => {
    setManageDrawer((prev) => ({ ...prev, open: false }));
  };

  const handleToggleColumn = (accessor) => {
    setTempVisibleColumns((prev) =>
      prev.includes(accessor) ? prev.filter((col) => col !== accessor) : [...prev, accessor]
    );
  };

  const handleToggleFilter = (id) => {
    setTempVisibleFilterIds((prev) =>
      prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]
    );
  };

  const handleApplyDrawer = () => {
    if (manageDrawer.type === "columns") {
      setVisibleColumns(tempVisibleColumns);
      localStorage.setItem(INSURANCE_COLUMN_VISIBILITY_KEY, JSON.stringify(tempVisibleColumns));
    } else {
      setVisibleFilterIds(tempVisibleFilterIds);
      localStorage.setItem(INSURANCE_FILTER_VISIBILITY_KEY, JSON.stringify(tempVisibleFilterIds));
    }
    handleCloseDrawer();
  };

  const handleResetDrawer = () => {
    if (manageDrawer.type === "columns") {
      setTempVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    } else {
      setTempVisibleFilterIds(DEFAULT_VISIBLE_FILTER_IDS);
    }
  };

  // Table columns & rows from custom table generator
  const { columns, rows } = insuranceTableData(
    insuranceList,
    handleViewModal,
    handleEditModal,
    handleDeleteModal,
    handleViewAttachments
  );

  const filteredVisibleColumns = useMemo(
    () => columns.filter((col) => visibleColumns.includes(col.accessor)),
    [columns, visibleColumns]
  );

  // Filter definitions for drawer and toolbar
  const filterDefinitions = useMemo(
    () => [
      { id: "product", label: "Product", type: "dropdown" },
      { id: "code", label: "Code", type: "dropdown" },
      { id: "insuranceCompany", label: "Insurance Company", type: "search" },
      { id: "month", label: "Month", type: "dropdown" },
      { id: "financialYear", label: "Financial Year", type: "dropdown" },
      { id: "policyStatus", label: "Policy Status", type: "dropdown" },
      { id: "customerName", label: "Customer Name", type: "search" },
      { id: "mobileNumber", label: "Mobile Number", type: "search" },
      { id: "policyNumber", label: "Policy Number", type: "search" },
      { id: "imd", label: "IMD", type: "search" },
      { id: "reference", label: "Reference", type: "search" },
    ],
    []
  );

  return (
    <>
      {/* Top Action Bar matching Lead Bay */}
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent={{ xs: "flex-start", md: "flex-end" }}
        flexWrap="wrap"
        gap={1.5}
        sx={{ width: "100%", mb: 2.5 }}
      >
        <Button
          variant="contained"
          onClick={() => setAddModalOpen(true)}
          startIcon={Icons.NEW}
          sx={{
            height: pxToRem(42),
            px: pxToRem(18),
            backgroundColor: "#0047B3",
            color: "#FFFFFF",
            borderRadius: pxToRem(6),
            boxShadow: "none",
            fontSize: pxToRem(14),
            fontWeight: 500,
            textTransform: "none",
            "& .MuiButton-startIcon": {
              color: "#FFFFFF !important",
            },
            "&:hover": {
              backgroundColor: "#003580",
              boxShadow: "none",
            },
          }}
        >
          Add Insurance
        </Button>

        <BasicButton
          title="Filters"
          icon="tune"
          background={Colors.WHITE}
          color={Colors.BLACK}
          action={() => handleOpenDrawer("filters")}
          ml={pxToRem(0)}
        />

        <BasicButton
          title="Columns"
          icon="view_column"
          background={Colors.WHITE}
          color={Colors.BLACK}
          action={() => handleOpenDrawer("columns")}
          ml={pxToRem(0)}
        />

        <BasicButton
          title="Export"
          icon={Icons.DOWNLOAD}
          background={Colors.WHITE}
          color={Colors.BLACK}
          action={handleExport}
          ml={pxToRem(0)}
        />

        <ResetFilterButton handleReset={handleResetFilters} marginTop={0} />
      </MDBox>

      {/* Dynamic Filter & Search Bar matching Lead Bay */}
      <MDBox
        display="flex"
        alignItems="start"
        flexWrap="wrap"
        sx={{ flexDirection: "row", gap: 1 }}
        style={{ width: "100%" }}
      >
        {visibleFilterIds.includes("product") && (
          <FilterDropdown
            label="Product"
            name="product"
            defaultValue={filters.product}
            value={filters.product}
            handleChange={handleFilterChange}
            menu={PRODUCT_FILTER_OPTIONS}
            minWidth={140}
          />
        )}

        {visibleFilterIds.includes("code") && (
          <FilterDropdown
            label="Code"
            name="code"
            defaultValue={filters.code}
            value={filters.code}
            handleChange={handleFilterChange}
            menu={CODE_FILTER_OPTIONS}
            minWidth={130}
          />
        )}

        {visibleFilterIds.includes("month") && (
          <FilterDropdown
            label="Month"
            name="month"
            defaultValue={filters.month}
            value={filters.month}
            handleChange={handleFilterChange}
            menu={MONTH_FILTER_OPTIONS}
            minWidth={130}
          />
        )}

        {visibleFilterIds.includes("financialYear") && (
          <FilterDropdown
            label="Financial Year"
            name="financialYear"
            defaultValue={filters.financialYear}
            value={filters.financialYear}
            handleChange={handleFilterChange}
            menu={FY_FILTER_OPTIONS}
            minWidth={130}
          />
        )}

        {visibleFilterIds.includes("policyStatus") && (
          <FilterDropdown
            label="Status"
            name="policyStatus"
            defaultValue={filters.policyStatus}
            value={filters.policyStatus}
            handleChange={handleFilterChange}
            menu={STATUS_FILTER_OPTIONS}
            minWidth={130}
          />
        )}

        <br />

        {visibleFilterIds.includes("insuranceCompany") && (
          <CustomSearch
            options={[]}
            value={searchQueries.insuranceCompany}
            debouncedHandleSearch={(e) => handleSearchChange("insuranceCompany", e.target.value)}
            placeholder="Search Company"
            label="Insurance Company"
            width={pxToRem(180)}
            marginRight={pxToRem(10)}
            freeSolos
          />
        )}

        {visibleFilterIds.includes("customerName") && (
          <CustomSearch
            options={[]}
            value={searchQueries.customerName}
            debouncedHandleSearch={(e) => handleSearchChange("customerName", e.target.value)}
            placeholder="Search Customer"
            label="Customer Name"
            width={pxToRem(180)}
            marginRight={pxToRem(10)}
            freeSolos
          />
        )}

        {visibleFilterIds.includes("mobileNumber") && (
          <CustomSearch
            options={[]}
            value={searchQueries.mobileNumber}
            debouncedHandleSearch={(e) => handleSearchChange("mobileNumber", e.target.value)}
            placeholder="Search Mobile"
            label="Mobile Number"
            width={pxToRem(180)}
            marginRight={pxToRem(10)}
            freeSolos
          />
        )}

        {visibleFilterIds.includes("policyNumber") && (
          <CustomSearch
            options={[]}
            value={searchQueries.policyNumber}
            debouncedHandleSearch={(e) => handleSearchChange("policyNumber", e.target.value)}
            placeholder="Search Policy No"
            label="Policy Number"
            width={pxToRem(180)}
            marginRight={pxToRem(10)}
            freeSolos
          />
        )}

        {visibleFilterIds.includes("imd") && (
          <CustomSearch
            options={[]}
            value={searchQueries.imd}
            debouncedHandleSearch={(e) => handleSearchChange("imd", e.target.value)}
            placeholder="Search IMD"
            label="IMD"
            width={pxToRem(160)}
            marginRight={pxToRem(10)}
            freeSolos
          />
        )}

        {visibleFilterIds.includes("reference") && (
          <CustomSearch
            options={[]}
            value={searchQueries.reference}
            debouncedHandleSearch={(e) => handleSearchChange("reference", e.target.value)}
            placeholder="Search Reference"
            label="Reference"
            width={pxToRem(160)}
            marginRight={pxToRem(10)}
            freeSolos
          />
        )}
      </MDBox>

      {/* Main Table */}
      <MDBox mt={2}>
        <DataTable
          table={{ columns: filteredVisibleColumns, rows }}
          isSorted={false}
          entriesPerPage={{ defaultValue: defaultData.PER_PAGE }}
          showTotalEntries={false}
          noEndBorder
          loading={loading}
          handleTablePagination={handleTablePagination}
          currentPage={tablePagination.page}
          handleCurrentPage={(page) => setTablePagination({ ...tablePagination, page })}
        />
      </MDBox>

      {/* Columns & Filters Management Drawer */}
      <Drawer
        anchor="right"
        open={manageDrawer.open}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: pxToRem(360) },
            borderRadius: 0,
            backgroundColor: "#FFFFFF",
            boxShadow: "0px 16px 40px rgba(16, 24, 40, 0.16)",
          },
        }}
      >
        <MDBox height="100%" display="flex" flexDirection="column">
          <MDBox
            px={2.5}
            py={2}
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <MDBox>
              <Typography sx={{ fontSize: pxToRem(16), fontWeight: 700, color: "#101828" }}>
                {manageDrawer.type === "columns" ? "Manage Columns" : "Manage Filters"}
              </Typography>
              <Typography sx={{ fontSize: pxToRem(12), color: "#667085", mt: 0.25 }}>
                {manageDrawer.type === "columns"
                  ? "Select columns to display in table"
                  : "Select filters to show above table"}
              </Typography>
            </MDBox>
            <IconButton size="small" onClick={handleCloseDrawer}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
          <Divider />

          <MDBox flex={1} px={2.5} py={1.5} sx={{ overflowY: "auto" }}>
            <FormGroup>
              {manageDrawer.type === "columns"
                ? columns.map((col) => (
                    <MDBox
                      key={col.accessor}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ minHeight: pxToRem(34) }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={tempVisibleColumns.includes(col.accessor)}
                            onChange={() => handleToggleColumn(col.accessor)}
                          />
                        }
                        label={col.Header}
                        sx={{
                          m: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: pxToRem(13),
                            color: "#344054",
                          },
                        }}
                      />
                      <Typography sx={{ color: "#D0D5DD", fontSize: pxToRem(16) }}>::</Typography>
                    </MDBox>
                  ))
                : filterDefinitions.map((filter) => (
                    <MDBox
                      key={filter.id}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ minHeight: pxToRem(34) }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={tempVisibleFilterIds.includes(filter.id)}
                            onChange={() => handleToggleFilter(filter.id)}
                          />
                        }
                        label={filter.label}
                        sx={{
                          m: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: pxToRem(13),
                            color: "#344054",
                          },
                        }}
                      />
                      <Typography sx={{ color: "#D0D5DD", fontSize: pxToRem(16) }}>::</Typography>
                    </MDBox>
                  ))}
            </FormGroup>
          </MDBox>

          <Divider />
          <MDBox px={2.5} py={2} display="flex" gap={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleResetDrawer}
              sx={{ textTransform: "none", color: "#344054", borderColor: "#D0D5DD" }}
            >
              Reset
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleApplyDrawer}
              sx={{ textTransform: "none", backgroundColor: "#0047B3", color: "#FFFFFF" }}
            >
              Apply
            </Button>
          </MDBox>
        </MDBox>
      </Drawer>

      {/* Add Insurance Modal */}
      {addModalOpen && (
        <AddInsuranceModal
          open={addModalOpen}
          handleClose={() => setAddModalOpen(false)}
          handleSubmit={handleConfirmCreate}
          loading={createLoading}
        />
      )}

      {/* Edit Insurance Modal */}
      {editModal.open && (
        <EditInsuranceModal
          open={editModal.open}
          insurance={editModal.insurance}
          handleClose={() => setEditModal({ open: false, insurance: null, loading: false })}
          handleSubmit={handleConfirmEdit}
          loading={editModal.loading}
        />
      )}

      {/* View Insurance Modal */}
      {viewModal.open && (
        <ViewInsuranceModal
          open={viewModal.open}
          insurance={viewModal.insurance}
          handleClose={() => setViewModal({ open: false, insurance: null })}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <ConfirmationModal
          open={deleteModal.open}
          handleClose={() => setDeleteModal({ open: false, insurance: null, loading: false })}
          title="Delete Insurance Policy"
          message={`Are you sure you want to delete policy ${
            deleteModal.insurance?.policyNumber
              ? `"${deleteModal.insurance.policyNumber}"`
              : `for ${deleteModal.insurance?.customerName || "this customer"}`
          }? This action cannot be undone.`}
          handleAction={handleConfirmDelete}
        />
      )}
    </>
  );
}
